import React, { useState, useEffect, useRef } from 'react';
import Confetti from '../ui/Confetti';
import { User, GameProgress } from '../../types';
import { api } from '../../utils/api';
import { playCorrectSound, playWrongSound, playGameOverSound, playSuccessSound, playKeyPressSound } from '../../utils/audio';

interface SearchOfThe67GameProps {
  user?: User;
}

const GAME_NAME = 'search-of-the-67';

const gameLevels = [
    // Level 1-10: Home Row & Simple Words
    { title: "Level 1: The First Step", description: "Type the letter that appears.", challenge: () => "a" },
    { title: "Level 2: Another Key", description: "Keep your fingers on the home row.", challenge: () => "s" },
    { title: "Level 3: The Path Unfolds", description: "Feel the rhythm.", challenge: () => "d" },
    { title: "Level 4: Keep Going", description: "You're doing great!", challenge: () => "f" },
    { title: "Level 5: The Other Hand", description: "Focus on your right hand now.", challenge: () => "j" },
    { title: "Level 6: Middle Finger", description: "Practice makes perfect.", challenge: () => "k" },
    { title: "Level 7: Almost There", description: "Stay focused.", challenge: () => "l" },
    { title: "Level 8: Pinky Power", description: "The last home row key.", challenge: () => ";" },
    { title: "Level 9: First Word", description: "Combine the letters.", challenge: () => "as" },
    { title: "Level 10: A Sad Word", description: "You can do it!", challenge: () => "sad" },
    // Level 11-20: Expanding Vocabulary
    { title: "Level 11: Longer Words", description: "Four letters now!", challenge: () => "fall" },
    { title: "Level 12: A Short Phrase", description: "Don't forget the space!", challenge: () => "a lad" },
    { title: "Level 13: Another Phrase", description: "Keep it up!", challenge: () => "ask dad" },
    { title: "Level 14: Top Row Intro", description: "Reach for the 'e' key.", challenge: () => "see" },
    { title: "Level 15: Reaching for 'i'", description: "Use your middle finger.", challenge: () => "is" },
    { title: "Level 16: Two Words", description: "Type the two words.", challenge: () => "it is" },
    { title: "Level 17: A Simple Question", description: "Look for the 'h'.", challenge: () => "he is" },
    { title: "Level 18: Another Pronoun", description: "Find the 'w'.", challenge: () => "we go" },
    { title: "Level 19: The Bottom Row", description: "Reach down for 'c'.", challenge: () => "cat" },
    { title: "Level 20: Man's Best Friend", description: "Find the 'g'.", challenge: () => "dog" },
    // Level 21-30: Simple Sentences
    { title: "Level 21: A Full Thought", description: "Remember the space bar.", challenge: () => "the cat sat" },
    { title: "Level 22: Another Animal", description: "Find the 'p' and 'g'.", challenge: () => "a pig" },
    { title: "Level 23: On the Farm", description: "Look for the 'h'.", challenge: () => "a hen" },
    { title: "Level 24: A Simple Action", description: "Type what you see.", challenge: () => "he can run" },
    { title: "Level 25: A State of Being", description: "Keep your accuracy high.", challenge: () => "she is sad" },
    { title: "Level 26: A Fun Activity", description: "This is fun!", challenge: () => "we can play" },
    { title: "Level 27: Look Up!", description: "Type the phrase.", challenge: () => "look at the sun" },
    { title: "Level 28: A Common Phrase", description: "You see this every day.", challenge: () => "how are you" },
    { title: "Level 29: A Simple Answer", description: "A positive response.", challenge: () => "I am good" },
    { title: "Level 30: Getting Tricky", description: "A longer sentence to test you.", challenge: () => "the dog can run fast" },
    // Level 31-40: Introducing Capitals and Punctuation
    { title: "Level 31: First Capital", description: "Use the Shift key!", challenge: () => "My name is Sam." },
    { title: "Level 32: A Question", description: "Don't forget the question mark.", challenge: () => "How are you today?" },
    { title: "Level 33: A Happy Thought", description: "Keep the positive vibe!", challenge: () => "The dog is very happy." },
    { title: "Level 34: A Love for Reading", description: "A good habit to have.", challenge: () => "She likes to read books." },
    { title: "Level 35: Finding Things", description: "Another question for you.", challenge: () => "Where is the big red ball?" },
    { title: "Level 36: An Invitation", description: "Let's go outside.", challenge: () => "Let's go to the park." },
    { title: "Level 37: A Fun Skill", description: "This is a useful skill.", challenge: () => "Coding is a lot of fun." },
    { title: "Level 38: The Golden Rule", description: "A motto to live by.", challenge: () => "Practice makes perfect." },
    { title: "Level 39: Wise Words", description: "Don't give up!", challenge: () => "Never give up on your dreams." },
    { title: "Level 40: A Famous Pangram", description: "This sentence has every letter!", challenge: () => "The quick brown fox jumps over the lazy dog." },
    // Level 41-50: More Complex Sentences
    { title: "Level 41: A Second Pangram", description: "Another sentence with every letter.", challenge: () => "Pack my box with five dozen liquor jugs." },
    { title: "Level 42: A Statement of Fact", description: "A simple truth about typing.", challenge: () => "I love to type on my keyboard!" },
    { title: "Level 43: School Life", description: "Education is key.", challenge: () => "School is important for learning." },
    { title: "Level 44: Getting to Know You", description: "Answer this question.", challenge: () => "What is your favorite subject?" },
    { title: "Level 45: A Simple Answer", description: "A common favorite.", challenge: () => "My favorite subject is science." },
    { title: "Level 46: A Science Fact", description: "A fact about our solar system.", challenge: () => "The Earth revolves around the Sun." },
    { title: "Level 47: Animal Kingdom", description: "A big animal fact.", challenge: () => "Elephants are the largest land animals." },
    { title: "Level 48: Let's Play!", description: "An invitation to play.", challenge: () => "Do you want to play a game?" },
    { title: "Level 49: The Right Answer", description: "Of course you do!", challenge: () => "Yes, let's play a typing game." },
    { title: "Level 50: The Real Goal", description: "This is the secret to success.", challenge: () => "Accuracy is more important than speed." },
    // Level 51-60: Advanced Punctuation and Structure
    { title: "Level 51: A Gentle Reminder", description: "Take it slow and steady.", challenge: () => "Take your time and focus on each key." },
    { title: "Level 52: A Quiet Place", description: "A peaceful location.", challenge: () => "The library is a quiet place to read." },
    { title: "Level 53: A Polite Request", description: "Remember your manners!", challenge: () => "Please, may I have some more water?" },
    { title: "Level 54: A Capital City", description: "A bit of geography.", challenge: () => "Washington, D.C. is the capital." },
    { title: "Level 55: A Famous Landmark", description: "A well-known statue.", challenge: () => "The Statue of Liberty is in New York." },
    { title: "Level 56: Another Pangram", description: "Can you type this one perfectly?", challenge: () => "Jumping zebras vexed a quiet fawn." },
    { title: "Level 57: A Wizard's Job", description: "A fantasy typing challenge.", challenge: () => "A wizard's job is to vex chumps quickly in fog." },
    { title: "Level 58: Quoted Speech", description: "Pay attention to the quotation marks!", challenge: () => 'He said, "Hello there!" with a big smile.' },
    { title: "Level 59: A Quoted Question", description: "More punctuation practice.", challenge: () => 'She asked, "What time is it?"' },
    { title: "Level 60: Numbers and Hyphens", description: "Don't miss the hyphen!", challenge: () => "The final score was 10-5, a great victory." },
    // Level 61-67: The Final Challenge
    { title: "Level 61: The Final Stretch", description: "Remember the rules of capitalization!", challenge: () => "Remember to use the Shift key for capital letters!" },
    { title: "Level 62: A Word of Encouragement", description: "You are doing an amazing job!", challenge: () => "This is getting quite challenging, but you can do it." },
    { title: "Level 63: An Email Address", description: "Watch for the '@' and '.' symbols.", challenge: () => "My email is example@typingmaster.org for help." },
    { title: "Level 64: A Website URL", description: "Type the full web address.", challenge: () => "The website is https://www.typingmaster.org - check it out!" },
    { title: "Level 65: Almost There", description: "You're so close to the end of your quest!", challenge: () => "Just a few more levels to go. Don't make a mistake now!" },
    { title: "Level 66: The Penultimate Key", description: "The final key is within your grasp. Focus and be precise.", challenge: () => "The final key is within your grasp. Focus and be precise." },
    { title: "Level 67: The Final Door", description: "You've arrived! Type the final code to unlock the door.", challenge: () => "Congratulations! You have found all 67 keys and completed the quest!" }
];

const SpriteHelper: React.FC<{ message: string }> = ({ message }) => (
    <div className="flex items-center gap-4 p-4 bg-blue-100 border-l-4 border-blue-500 rounded-r-lg">
        <div className="text-5xl">🦉</div>
        <p className="text-blue-800 font-semibold">{message}</p>
    </div>
);

const SearchOfThe67Game: React.FC<SearchOfThe67GameProps> = ({ user }) => {
    const [gameState, setGameState] = useState<'ready' | 'playing' | 'gameOver' | 'victory'>('ready');
    const [progress, setProgress] = useState<GameProgress>({ high_score: 0, total_attempts: 0 });
    const [currentLevelIndex, setCurrentLevelIndex] = useState(0);
    const [keysCollected, setKeysCollected] = useState(0);
    const [playerInput, setPlayerInput] = useState("");
    const [mistakes, setMistakes] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);

    const MAX_MISTAKES = 3;

    useEffect(() => {
        if (user && user.user_id !== 'guest') {
            api.get<GameProgress>(`/api/v1/student/game-progress/${GAME_NAME}`)
                .then(data => setProgress(data || { high_score: 0, total_attempts: 0 }))
                .catch(err => console.error("Failed to load game progress", err));
        }
    }, [user]);

    const startGame = () => {
        if (user && user.user_id !== 'guest') {
            const newAttempts = progress.total_attempts + 1;
            setProgress(p => ({ ...p, total_attempts: newAttempts }));
            api.post('/api/v1/student/game-progress', { game_name: GAME_NAME, increment_attempts: true });
        }

        setCurrentLevelIndex(0);
        setKeysCollected(0);
        setPlayerInput("");
        setMistakes(0);
        setGameState('playing');
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (gameState !== 'playing') return;

        const typedValue = e.target.value;
        const expectedText = gameLevels[currentLevelIndex].challenge();
        
        playKeyPressSound();

        if (expectedText.startsWith(typedValue)) {
            setPlayerInput(typedValue);
            if (typedValue === expectedText) {
                // Correctly completed the level
                playSuccessSound();
                if (currentLevelIndex === gameLevels.length - 1) {
                    // Victory!
                    setGameState('victory');
                    if (user && user.user_id !== 'guest') {
                        const newHighScore = Math.max(progress.high_score, gameLevels.length);
                        setProgress(p => ({...p, high_score: newHighScore}));
                        api.post('/api/v1/student/game-progress', { game_name: GAME_NAME, high_score: newHighScore });
                    }
                } else {
                    // Advance to next level
                    setCurrentLevelIndex(i => i + 1);
                    setKeysCollected(k => k + 1);
                    setPlayerInput("");
                }
            }
        } else {
            // Mistake made
            playWrongSound();
            const newMistakes = mistakes + 1;
            setMistakes(newMistakes);

            if (newMistakes >= MAX_MISTAKES) {
                playGameOverSound();
                if (user && user.user_id !== 'guest') {
                    const newHighScore = Math.max(progress.high_score, keysCollected);
                    setProgress(p => ({ ...p, high_score: newHighScore }));
                    api.post('/api/v1/student/game-progress', { game_name: GAME_NAME, high_score: newHighScore });
                }
                setGameState('gameOver');
            } else {
                // Reset current challenge input, but don't end the game
                setPlayerInput("");
            }
        }
    };
    
    const renderChallengeText = () => {
        const expected = gameLevels[currentLevelIndex].challenge();
        return expected.split('').map((char, index) => (
            <span key={index} className={index < playerInput.length ? 'text-green-600' : 'text-gray-500'}>
                {char}
            </span>
        ));
    };

    if (gameState === 'ready') {
        return (
            <div className="text-center p-8">
                <SpriteHelper message="Welcome to 'In Search of the 67'! Your quest is to type perfectly through 67 levels. You have 3 lives. Are you ready?" />
                {user && user.user_id !== 'guest' && (
                    <div className="mt-4">
                        <p>High Score: {progress.high_score} Keys | Total Attempts: {progress.total_attempts}</p>
                    </div>
                )}
                <button onClick={startGame} className="mt-6 px-8 py-4 bg-blue-600 text-white font-bold text-xl rounded-lg shadow-lg hover:bg-blue-700 transition">
                    Start Your Quest
                </button>
            </div>
        );
    }
    
     if (gameState === 'gameOver') {
        return (
            <div className="text-center p-8">
                <SpriteHelper message="Oh no, too many mistakes! The path is treacherous, but every great adventurer stumbles. Rest and try again." />
                <h2 className="text-6xl font-bold text-red-500 my-8">Game Over!</h2>
                 <div className="mt-4">
                    <p>You collected {keysCollected} keys this attempt.</p>
                    {user && user.user_id !== 'guest' && <p>Your high score is {progress.high_score} keys.</p>}
                </div>
                <button onClick={startGame} className="mt-6 px-8 py-4 bg-blue-600 text-white font-bold text-xl rounded-lg shadow-lg hover:bg-blue-700 transition">
                    Try Again
                </button>
            </div>
        );
    }
    
     if (gameState === 'victory') {
        return (
            <div className="text-center p-8">
                <Confetti />
                <SpriteHelper message="You did it! You are a true typing master! You have found the legendary 67 keys and completed the quest!" />
                <h2 className="text-6xl font-bold text-yellow-500 my-8">VICTORY!</h2>
                 <div className="mt-4">
                    <p>You collected all 67 keys!</p>
                </div>
                <button onClick={startGame} className="mt-6 px-8 py-4 bg-blue-600 text-white font-bold text-xl rounded-lg shadow-lg hover:bg-blue-700 transition">
                    Play Again
                </button>
            </div>
        );
    }

    const currentLevel = gameLevels[currentLevelIndex];
    return (
        <div className="p-4" onClick={() => inputRef.current?.focus()}>
            <div className="flex justify-between items-center mb-2">
                <div className="text-lg"><strong>Level {currentLevelIndex + 1}:</strong> {currentLevel.title}</div>
                <div className="flex items-center gap-4 text-lg font-bold">
                    <div className="flex items-center gap-1 text-red-500" title={`${MAX_MISTAKES - mistakes} lives remaining`}>
                        <span>❤️</span>
                        <span>{MAX_MISTAKES - mistakes}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <span>🔑</span>
                        <span>{keysCollected} / 67</span>
                    </div>
                </div>
            </div>
            <div className="w-full h-2.5 bg-gray-200 rounded-full overflow-hidden mb-4">
                <div className="h-full bg-gradient-to-r from-purple-500 to-blue-500 rounded-full transition-all duration-500" style={{ width: `${(keysCollected / 67) * 100}%` }} />
            </div>

            <SpriteHelper message={currentLevel.description} />

            <div className="my-8 p-6 bg-gray-100 rounded-lg text-center font-mono text-4xl tracking-widest h-24 flex items-center justify-center">
                {renderChallengeText()}
            </div>
            
            <input
                ref={inputRef}
                type="text"
                value={playerInput}
                onChange={handleInputChange}
                className="w-full p-4 border-2 border-gray-300 rounded-lg text-center text-2xl font-mono focus:border-blue-500 focus:ring-blue-500"
                autoFocus
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
            />
            {user && user.user_id !== 'guest' && (
                <div className="text-center mt-4 text-sm text-gray-500">
                    <p>High Score: {progress.high_score} Keys | Attempts: {progress.total_attempts}</p>
                </div>
            )}
        </div>
    );
};

export default SearchOfThe67Game;